import Avatar from '@/components/UI/Avatar';
import type { BaseUser } from '@/model/User.model';
import { type CSSProperties, type FC, useMemo } from 'react';
import { _isBlank } from '@/littledash';
import type { AvatarProps } from '../Avatar/Avatar';

export interface UserAvatarProps extends AvatarProps {
  user?: Pick<BaseUser, 'name'>;
  tooltip?: string;
  dataTestId?: string;
}

/**
 * Derives up-to-two-letter initials from a user's display name (first + last
 * word initial), upper-cased. Returns "-" for a missing or blank name.
 */
export const getInitials = (name?: string): string => {
  if (_isBlank(name)) {
    return '-';
  }
  const matches = name?.match(/\b\w/g);
  return `${matches?.shift() ?? ''}${matches?.pop() ?? ''}`.toUpperCase();
};

const UserAvatar: FC<UserAvatarProps> = ({
  user,
  tooltip = '',
  bgColorClass = 'bg-dark-gray',
  includeName = false,
  dataTestId,
  className = '',
  ...props
}) => {
  const initials = useMemo(() => getInitials(user?.name), [user?.name]);

  return (
    <span data-testid={dataTestId}>
      {' '}
      <Avatar tooltip={tooltip} bgColorClass={bgColorClass} className={className} {...props}>
        {initials}
      </Avatar>
    </span>
  );
};

interface TruncatedAvatarProps {
  remainder: number;
  onClick?: () => void;
  style?: CSSProperties;
  className?: string;
  dataTestId?: string;
}

export const TruncatedAvatar = ({ remainder, onClick, style, className = '', dataTestId }: TruncatedAvatarProps) => (
  <span data-testid={dataTestId}>
    <div className={`tc w2 h2 dib br-100 bg-dark-gray ${className}`} onClick={onClick} style={style}>
      <div className="flex justify-center items-center w-100 h-100 normal f6 lh-title white">
        <span>+</span> {remainder}
      </div>
    </div>
  </span>
);

export default UserAvatar;
